import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import { DataTable, EmptyState, PageHeader, useNotify } from '@routes-plan/ui';
import { Bank } from '../../../models/bank';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { setPageTitle } from '../../../utils/pageTitle';
import { useT } from '../../../i18n';
import { AdminPage, ContentCard, RowAction, RowActions, apiError, useConfirm } from '../shared/AdminUi';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface BankRow {
  id: number;
  name: string;
  country: string;
}

const BankList: React.FC<ShowAlerts> = () => {
  setPageTitle('menu.admin.banks');
  const t = useT();
  const notify = useNotify();
  const [confirm, confirmDialog] = useConfirm();
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState(true);
  const { canCreate, canUpdate, canDelete } = useResourcePermissions('/admin/banks');

  useEffect(() => {
    const fetchBanks = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get('banks');
        setBanks(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        notify.error(t('banks.loadError'));
      } finally {
        setLoading(false);
      }
    };
    fetchBanks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteBank = async (row: BankRow) => {
    if (!canDelete) return;
    const ok = await confirm({ title: t('banks.confirmDeleteTitle', { name: row.name }), message: t('banks.confirmDelete') });
    if (!ok) return;
    try {
      await axios.delete(`banks/${row.id}`);
      setBanks((prev) => prev.filter((b) => b.id !== row.id));
      notify.success(t('banks.deletedOk'));
    } catch (error) {
      notify.error(apiError(error, t('banks.deleteError')));
    }
  };

  const rows: BankRow[] = banks.map((bank) => ({
    id: bank.id,
    name: bank.name,
    country: bank.country?.name || t('banks.noCountry'),
  }));

  const addButton = (
    <PermissionButton
      allowed={canCreate}
      deniedTooltip={t('common.noPermissionCreate')}
      variant="contained"
      startIcon={<AddIcon />}
      component={NavLink}
      to="/admin/banks/new"
    >
      {t('banks.newTitle')}
    </PermissionButton>
  );

  return (
    <AdminPage>
      <PageHeader title={t('banks.title')} description={t('banks.subtitle')} actions={addButton} />
      <ContentCard>
        <DataTable<BankRow>
          rows={rows}
          loading={loading}
          getRowId={(r) => r.id}
          ariaLabel={t('banks.title')}
          searchable
          searchPlaceholder={t('banks.searchPlaceholder')}
          exportFileName={t('banks.exportFileName')}
          initialSort={{ columnId: 'name', direction: 'asc' }}
          columns={[
            { id: 'name', header: t('banks.bank'), value: (r) => r.name, sortable: true },
            { id: 'country', header: t('banks.country'), value: (r) => r.country, sortable: true },
          ]}
          rowActions={(row) => (
            <RowActions>
              <RowAction
                label={`${t('common.edit')} ${row.name}`}
                icon={<EditOutlinedIcon fontSize="small" />}
                allowed={canUpdate}
                deniedTooltip={t('common.noPermissionEdit')}
                to={`/admin/banks/${row.id}/edit`}
              />
              <RowAction
                label={`${t('common.delete')} ${row.name}`}
                icon={<DeleteOutlineIcon fontSize="small" />}
                color="error"
                allowed={canDelete}
                deniedTooltip={t('common.noPermissionDelete')}
                onClick={() => handleDeleteBank(row)}
              />
            </RowActions>
          )}
          emptyState={
            <EmptyState
              icon={<AccountBalanceOutlinedIcon />}
              title={t('banks.empty')}
              description={t('banks.emptyDescription')}
              action={canCreate ? addButton : undefined}
            />
          }
        />
      </ContentCard>
      {confirmDialog}
    </AdminPage>
  );
};

export default BankList;
